import type { Moment } from "moment";
import moment from "moment";

import { Observable } from "../../../utils/observable";
import { getPeriod } from "../../../utils/period-fetcher";
import { TransactionDirection } from "../transaction/transaction-filter";
import { TimeRange } from "./timerange";

export interface TimeRangeSelection {
  timeRange: TimeRange;
  bounds: [Moment | null, Moment | null];
}

const NO_BOUNDS: [null, null] = [null, null];

/** Which pending-categorisation subset the list is restricted to. */
export enum JustificationFilter {
  ALL = "All",
  TO_JUSTIFY = "ToJustify",
  TO_QUALIFY = "ToQualify",
}

export class TransactionSearchManager {
  public timeRangeSelection = new Observable<TimeRangeSelection>({
    timeRange: TimeRange.Quarter,
    bounds: this.getTimeBounds(TimeRange.Quarter),
  });
  public absoluteAmountSelection = new Observable<[number | null, number | null]>(NO_BOUNDS);
  public directionSelection = new Observable<TransactionDirection>(TransactionDirection.ALL);
  public amountRange = new Observable<[number | null, number | null]>(NO_BOUNDS);
  public justificationSelection = new Observable<JustificationFilter>(JustificationFilter.ALL);

  setTimeRange(timeRange: TimeRange, bounds?: [Moment | null, Moment | null]) {
    if (timeRange === TimeRange.Other) {
      this.timeRangeSelection.set({ timeRange: TimeRange.Other, bounds: bounds ? bounds : NO_BOUNDS });
    } else {
      this.timeRangeSelection.set({ timeRange, bounds: this.getTimeBounds(timeRange) });
    }
  }

  setAmountRange(low: number | null, high: number | null) {
    this.amountRange.set([low, high]);
  }

  setTransactionDirection(direction: TransactionDirection) {
    this.directionSelection.set(direction);
  }

  setJustificationFilter(justification: JustificationFilter) {
    this.justificationSelection.set(justification);
  }

  private getTimeBounds(timeRange: Exclude<TimeRange, TimeRange.Other>): [Moment | null, Moment | null] {
    const today = moment.utc().endOf("day");
    const todayRef = moment.utc().endOf("day");
    switch (timeRange) {
      case TimeRange.Quarter: {
        const { startDate, endDate } = getPeriod("previous", today);
        return [startDate, endDate];
      }
      case TimeRange.Week:
        return [todayRef.subtract(7, "days"), today];
      case TimeRange.Month:
        return [todayRef.subtract(1, "months"), today];
    }
  }

  public clear() {
    this.timeRangeSelection.set({ timeRange: TimeRange.Quarter, bounds: NO_BOUNDS });
    this.absoluteAmountSelection.set(NO_BOUNDS);
    this.amountRange.set(NO_BOUNDS);
    this.directionSelection.set(TransactionDirection.ALL);
    this.justificationSelection.set(JustificationFilter.ALL);
  }
}
